import React, { useEffect, useState } from 'react';
import {
  CircularProgress,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import {
  doOptionsContainValue,
  getLabelFromOptionValues,
  Option,
} from '@/components/Filter/FilterRow/FilterRow.tsx';
import { FilterRowSelection } from '@lambda-layer/types/filter.ts';

export interface Props {
  label?: string;
  isLoading?: boolean;
  filterRowSelection: FilterRowSelection;
  onChange: (filterRowSelection: FilterRowSelection) => void;
  options: Option[];
  onFocusChange?: () => void;
  isMetaField?: boolean;
  labelMapping?: Record<string, string>;
  autoFocus?: boolean;
}

const FilterRowSelect: React.FC<Props> = ({
  label,
  isLoading = false,
  filterRowSelection,
  onChange,
  options = [],
  onFocusChange,
  isMetaField,
  labelMapping,
  autoFocus = false,
}) => {
  const handleSelectChange = (
    event: SelectChangeEvent<FilterRowSelection['value'][0]>
  ) => {
    const updatedValue = { ...filterRowSelection, value: [event.target.value] };
    onChange(updatedValue);
  };

  const selectId = `${label?.toLowerCase().replace(/\s+/g, '-')}-select`;

  const [open, setOpen] = useState(false);

  // Trigger onFocusChange on mount if a default value exists,
  // similar to how the multi-select triggers focus events.
  useEffect(() => {
    if (
      filterRowSelection.value &&
      filterRowSelection.value.length > 0 &&
      onFocusChange
    ) {
      onFocusChange();
    }
  }, [filterRowSelection.value, onFocusChange]);

  const renderSelectOptions = () => {
    return options.map(option => (
      <MenuItem key={option.value} value={option.value || ''}>
        <ListItemText
          primary={`${getLabelFromOptionValues(labelMapping, [option.value])} ${
            !isMetaField ? '(' + option.count + ')' : ''
          }`}
        />
      </MenuItem>
    ));
  };

  const renderLoadingIndicator = () => (
    <MenuItem disabled>
      <CircularProgress size={20} />
      <Typography ml={2}>Loading...</Typography>
    </MenuItem>
  );

  return (
    <Select<FilterRowSelection['value'][0]>
      autoFocus={autoFocus}
      id={selectId}
      variant="outlined"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={
        doOptionsContainValue(options, filterRowSelection.value)
          ? filterRowSelection.value[0]
          : ''
      }
      onChange={handleSelectChange}
      onFocus={onFocusChange}
      onBlur={onFocusChange}
      renderValue={selected => {
        if (selected === undefined || selected === null) {
          return '';
        }
        if (labelMapping) {
          return labelMapping[selected.toString()];
        }
        return selected.toString();
      }}
      displayEmpty
    >
      {/* Keep options during loading while open to avoid menu flicker/close */}
      {isLoading && !open ? renderLoadingIndicator() : renderSelectOptions()}
    </Select>
  );
};

export default FilterRowSelect;
